import * as React from "react";
import {ChangeEventHandler, useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Box, SelectChangeEvent} from "@mui/material";
import dayjs from "dayjs";
import {QuoteStatus, RequestFilter, RequestStatus} from "@/api/data-contracts";
import {Constants} from "@/api/constants";
import {RequestStatusBadge} from "@/common/RequestStatusBadge";
import {QuoteStatusBadge} from "@/common/QuoteStatusBadge";
import MultiSelectInputField from "@/lib/components/inputfields/multi-select-input-field";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import {NamedValueHandler} from "@/lib/modelstate";
import {quoteStatusOptions, requestStatusOptions} from "@/datacaches";
import {CommonI18N, OverviewTableI18N, RequestI18N} from "@/translations";
import {RequestOverviewFilters} from "@/common/overview-state/request-view-state";
import {REQUEST_OVERVIEW_COLUMN_IDS} from "@/common/overview-table/overview-table-column-ids";
import {renderRequestRangeColumnFilter} from "@/pages/request-overview-page/RequestRangeColumnFilters";
import {isOperatorRole, useAuth} from "@/app/auth/AuthContext";

interface RequestColumnFilterProps {
    /** The applied filters (Sofortwirkung) — read-only here, every change goes back through `onChange`. */
    filters: RequestOverviewFilters;
    /** Wired to `setFilters` by the caller — never writes onto the applied state directly. */
    onChange: (partial: Partial<RequestOverviewFilters>) => void;
}

/** `undefined`/`true`/`false` <-> the three sentinel strings the tri-state `SelectInputField` renders,
 *  used by this module's own "Info" column filter. */
type TriStateSelectValue = "all" | "true" | "false";

function toTriStateSelectValue(value: boolean | undefined): TriStateSelectValue {
    if (value === true) {
        return "true";
    }
    if (value === false) {
        return "false";
    }
    return "all";
}

function fromTriStateSelectValue(raw: string): boolean | undefined {
    if (raw === "true") {
        return true;
    }
    if (raw === "false") {
        return false;
    }
    return undefined;
}

/** `undefined`/`true` <-> the two sentinel strings the two-option `hasUnreadMessages` select renders (#1836).
 *  Anything other than `true`, including a stale stored `false`, maps to "all" so the select never blanks. */
type UnreadMessagesSelectValue = "all" | "true";

function toUnreadMessagesSelectValue(value: boolean | undefined): UnreadMessagesSelectValue {
    return value === true ? "true" : "all";
}

/**
 * Curried numeric-field handler — one `useCallback` per contract field without repeating its body.
 * Exported: this module's own range column filters live in `RequestRangeColumnFilters.tsx`.
 */
export function useNumberFieldChange(
    field: keyof RequestOverviewFilters,
    onChange: (partial: Partial<RequestOverviewFilters>) => void
): NamedValueHandler {
    return useCallback(
        ({value}) =>
            onChange({[field]: typeof value === "number" ? value : undefined} as Partial<RequestOverviewFilters>),
        [field, onChange]
    );
}

/** Curried date-field handler — exported for the same reason as `useNumberFieldChange`. */
export function useDateFieldChange(
    field: keyof RequestOverviewFilters,
    onChange: (partial: Partial<RequestOverviewFilters>) => void
): NamedValueHandler {
    return useCallback(
        ({value}) =>
            onChange({
                [field]: value instanceof Date ? dayjs(value).format("YYYY-MM-DD") : undefined
            } as Partial<RequestOverviewFilters>),
        [field, onChange]
    );
}

/**
 * Curried text-field handler for the four column text filters: the two Enthält-Suche fields
 * (`customerNumberContains`, `requesterCompanyContains`) and the two Anfangs-Suche fields
 * (`cartonGradeStartsWith`, `fefcoCodeStartsWith`).
 */
function useTextFieldChange(
    field: keyof RequestOverviewFilters,
    onChange: (partial: Partial<RequestOverviewFilters>) => void
): ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> {
    return useCallback(
        (event) =>
            onChange({
                [field]: event.target.value === "" ? undefined : event.target.value
            } as Partial<RequestOverviewFilters>),
        [field, onChange]
    );
}

/** Curried tri-state handler for `hasPrintImages` / `hasBluePrints`. */
function useTriStateFieldChange(
    field: keyof RequestOverviewFilters,
    onChange: (partial: Partial<RequestOverviewFilters>) => void
): ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> {
    return useCallback(
        (event) =>
            onChange({[field]: fromTriStateSelectValue(event.target.value)} as Partial<RequestOverviewFilters>),
        [field, onChange]
    );
}

/**
 * The status column's filter content: `requestStatuses` for the Anfragesteller-/Innendienstsicht
 * (`type === INTERNAL`), `quoteStatuses` for the Angebotsgebersicht.
 */
const RequestStatusColumnFilter: React.FC<RequestColumnFilterProps & {type: RequestFilter}> = ({
    type,
    filters,
    onChange
}) => {
    const {t} = useTranslation();
    const isInternal = type === RequestFilter.INTERNAL;

    const requestStatusOptionsLabeled = useMemo(
        () => requestStatusOptions.map((s) => ({label: t(CommonI18N.enum.requestStatus[s]), value: s})),
        [t]
    );
    const quoteStatusOptionsLabeled = useMemo(
        () => quoteStatusOptions.map((s) => ({label: t(CommonI18N.enum.quoteStatus[s]), value: s})),
        [t]
    );

    const handleRequestStatusesChange = useCallback(
        (event: SelectChangeEvent<unknown[]>) => {
            const raw = event.target.value;
            const next = (Array.isArray(raw) ? raw : []).filter((v): v is RequestStatus =>
                requestStatusOptions.includes(v as RequestStatus)
            );
            onChange({requestStatuses: next});
        },
        [onChange]
    );

    const handleQuoteStatusesChange = useCallback(
        (event: SelectChangeEvent<unknown[]>) => {
            const raw = event.target.value;
            const next = (Array.isArray(raw) ? raw : []).filter((v): v is QuoteStatus =>
                quoteStatusOptions.includes(v as QuoteStatus)
            );
            onChange({quoteStatuses: next});
        },
        [onChange]
    );

    return (
        <Box sx={{minWidth: 220}}>
            {isInternal ? (
                <MultiSelectInputField
                    name="requestStatuses"
                    label={t(RequestI18N.details.field.status)}
                    value={filters.requestStatuses ?? []}
                    onChange={handleRequestStatusesChange}
                    options={requestStatusOptionsLabeled}
                    renderOption={(option) => <RequestStatusBadge key={String(option.value)} status={option.value} />}
                />
            ) : (
                <MultiSelectInputField
                    name="quoteStatuses"
                    label={t(RequestI18N.details.field.status)}
                    value={filters.quoteStatuses ?? []}
                    onChange={handleQuoteStatusesChange}
                    options={quoteStatusOptionsLabeled}
                    renderOption={(option) => <QuoteStatusBadge key={String(option.value)} status={option.value} />}
                />
            )}
        </Box>
    );
};

/** The "Anfragenummer" column's filter content: the Enthält-Suche on `customerNumberContains`. */
const RequestNumberColumnFilter: React.FC<RequestColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const handleChange = useTextFieldChange("customerNumberContains", onChange);

    return (
        <Box sx={{minWidth: 220}}>
            <StringInputField
                name="customerNumberContains"
                label={t(RequestI18N.details.field.customerRequestNumber)}
                value={filters.customerNumberContains ?? ""}
                onChange={handleChange}
                maxChars={Constants.REQUEST_SEARCH_TEXT_LENGTH_MAX}
            />
        </Box>
    );
};

/**
 * The "Anfragesteller" column's filter content — the Angebotsgebersicht and, since #1604, the
 * Innendienstsicht: the column itself exists only there (Constraint 6), so the column's own presence is the
 * single gate, `renderRequestColumnFilter` below renders this content unconditionally.
 */
const RequesterCompanyColumnFilter: React.FC<RequestColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const handleChange = useTextFieldChange("requesterCompanyContains", onChange);

    return (
        <Box sx={{minWidth: 220}}>
            <StringInputField
                name="requesterCompanyContains"
                label={t(RequestI18N.details.field.requesterCompany)}
                value={filters.requesterCompanyContains ?? ""}
                onChange={handleChange}
                maxChars={Constants.REQUEST_SEARCH_TEXT_LENGTH_MAX}
            />
        </Box>
    );
};

/**
 * The "Sorte" column's filter content: an Anfangs-Suche text field on `cartonGradeStartsWith`, plus a
 * tri-state Ja/Nein-Auswahl on `isProGroupCartonGrade` (ProGroup vs. customized, standing constraint 12),
 * stacked the way `RequestInfoColumnFilter` stacks its two selects.
 */
const RequestCartonGradeColumnFilter: React.FC<RequestColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const handleChange = useTextFieldChange("cartonGradeStartsWith", onChange);
    const handleIsProGroupCartonGradeChange = useTriStateFieldChange("isProGroupCartonGrade", onChange);

    const triStateOptions = useMemo(
        () => [
            {label: t(OverviewTableI18N.field.triStateAll), value: "all"},
            {label: t(CommonI18N.message.yes), value: "true"},
            {label: t(CommonI18N.message.no), value: "false"}
        ],
        [t]
    );

    return (
        <Box sx={{minWidth: 220, display: "flex", flexDirection: "column", gap: 2}}>
            <StringInputField
                name="cartonGradeStartsWith"
                label={t(RequestI18N.details.field.variety)}
                value={filters.cartonGradeStartsWith ?? ""}
                onChange={handleChange}
                maxChars={Constants.REQUEST_SEARCH_TEXT_LENGTH_MAX}
            />
            <SelectInputField
                name="isProGroupCartonGrade"
                label={t(RequestI18N.details.field.proGroupCartonGrade)}
                value={toTriStateSelectValue(filters.isProGroupCartonGrade)}
                onChange={handleIsProGroupCartonGradeChange}
                options={triStateOptions}
            />
        </Box>
    );
};

/** The "FEFCO" column's filter content: an Anfangs-Suche text field on `fefcoCodeStartsWith`. */
const RequestFefcoColumnFilter: React.FC<RequestColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const handleChange = useTextFieldChange("fefcoCodeStartsWith", onChange);

    return (
        <Box sx={{minWidth: 220}}>
            <StringInputField
                name="fefcoCodeStartsWith"
                label={t(RequestI18N.details.field.fefcoCode)}
                value={filters.fefcoCodeStartsWith ?? ""}
                onChange={handleChange}
                maxChars={Constants.REQUEST_SEARCH_TEXT_LENGTH_MAX}
            />
        </Box>
    );
};

/**
 * The "Info" column's filter content: the Ja/Nein-Auswahlen `hasPrintImages` and `hasBluePrints`, plus a
 * two-option `hasUnreadMessages` select since #1604/#1836 (constraint 12 of the plan, the info column now
 * carries three field groups). No sort entries, the column stays unsortable (plan "Standing constraints",
 * Entscheidung 5). The third select is hidden for an Innendienst-Mitarbeiter (constraint 7 of the plan):
 * that role never receives an unread count above 0, so offering the control would only ever narrow to an
 * empty result. Unlike `hasPrintImages`/`hasBluePrints`, `hasUnreadMessages` offers only "Alle" and "Nur
 * ungelesene Nachrichten" (#1836): there is no explicit "Nein" any more, a stale stored `false` renders as
 * "Alle" via `toUnreadMessagesSelectValue`.
 */
const RequestInfoColumnFilter: React.FC<RequestColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const {roles} = useAuth();
    const isOperator = isOperatorRole(roles);
    const handleHasPrintImagesChange = useTriStateFieldChange("hasPrintImages", onChange);
    const handleHasBluePrintsChange = useTriStateFieldChange("hasBluePrints", onChange);
    const handleHasUnreadMessagesChange = useTriStateFieldChange("hasUnreadMessages", onChange);

    const triStateOptions = useMemo(
        () => [
            {label: t(OverviewTableI18N.field.triStateAll), value: "all"},
            {label: t(CommonI18N.message.yes), value: "true"},
            {label: t(CommonI18N.message.no), value: "false"}
        ],
        [t]
    );

    const unreadMessagesOptions = useMemo(
        () => [
            {label: t(OverviewTableI18N.field.triStateAll), value: "all"},
            {label: t(OverviewTableI18N.field.onlyUnreadMessages), value: "true"}
        ],
        [t]
    );

    return (
        <Box sx={{minWidth: 220, display: "flex", flexDirection: "column", gap: 2}}>
            <SelectInputField
                name="hasPrintImages"
                label={t(RequestI18N.details.field.hasPrintImages)}
                value={toTriStateSelectValue(filters.hasPrintImages)}
                onChange={handleHasPrintImagesChange}
                options={triStateOptions}
            />
            <SelectInputField
                name="hasBluePrints"
                label={t(RequestI18N.details.field.hasBlueprints)}
                value={toTriStateSelectValue(filters.hasBluePrints)}
                onChange={handleHasBluePrintsChange}
                options={triStateOptions}
            />
            {!isOperator && (
                <SelectInputField
                    name="hasUnreadMessages"
                    label={t(OverviewTableI18N.field.hasUnreadMessages)}
                    value={toUnreadMessagesSelectValue(filters.hasUnreadMessages)}
                    onChange={handleHasUnreadMessagesChange}
                    options={unreadMessagesOptions}
                />
            )}
        </Box>
    );
};

/**
 * Renders one request column's filter content for `DataTable`'s `renderColumnFilter` (plan "Spaltenkopf-
 * Filterung und Sortieranzeige", Phase 3.1, Standing constraint 2) — looked up by `columnId`
 * (`meta.filterColumnId`). `type` decides the status field's contract field (`requestStatuses` vs.
 * `quoteStatuses`). Every `columnId` not matched by the select/text cases below falls through to
 * `renderRequestRangeColumnFilter` (`RequestRangeColumnFilters.tsx`, Phase 3.2), which covers the range
 * columns the same way.
 */
export function renderRequestColumnFilter(
    columnId: string,
    type: RequestFilter,
    filters: RequestOverviewFilters,
    onChange: (partial: Partial<RequestOverviewFilters>) => void
): React.ReactNode | undefined {
    switch (columnId) {
        case REQUEST_OVERVIEW_COLUMN_IDS.status:
            return <RequestStatusColumnFilter type={type} filters={filters} onChange={onChange} />;
        case REQUEST_OVERVIEW_COLUMN_IDS.customerRequestNumber:
            return <RequestNumberColumnFilter filters={filters} onChange={onChange} />;
        case REQUEST_OVERVIEW_COLUMN_IDS.requesterCompany:
            return <RequesterCompanyColumnFilter filters={filters} onChange={onChange} />;
        case REQUEST_OVERVIEW_COLUMN_IDS.cartonGrade:
            return <RequestCartonGradeColumnFilter filters={filters} onChange={onChange} />;
        case REQUEST_OVERVIEW_COLUMN_IDS.fefcoCode:
            return <RequestFefcoColumnFilter filters={filters} onChange={onChange} />;
        case REQUEST_OVERVIEW_COLUMN_IDS.info:
            return <RequestInfoColumnFilter filters={filters} onChange={onChange} />;
        default:
            // The range columns (Innenmaße, Anfragemenge, Netto, Gesamtpreis, Gültig bis, Lieferzeitraum,
            // and, Angebotsgebersicht only, Entfernung — Phase 3.2) live in their own module, kept apart
            // from the select/text columns above so this switch does not outgrow a readable size.
            return renderRequestRangeColumnFilter(columnId, type, filters, onChange);
    }
}
